import clsx from 'clsx'
import { observer } from 'mobx-react-lite'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import TextHeadline from '@/src/components/ui/texts/TextHeadline'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { store } from '@/src/stores/store'

const TAP_HINT_REVEAL_DELAY_MS = 2_000

export type TutorialOverlayVariant = 'shield' | 'snap' | 'plane'

type TutorialOverlayConfig = {
    titleLine1: string
    titleLine2: string
    body: string
    icon: string
}

const TUTORIAL_OVERLAY_CONFIG: Record<TutorialOverlayVariant, TutorialOverlayConfig> = {
    shield: {
        titleLine1: 'Schutzschild',
        titleLine2: 'eingesammelt',
        body: 'Schützt dich vor dem nächsten Hindernis!',
        icon: '/images/powerups/PowerUp_Shield.png',
    },
    snap: {
        titleLine1: 'Unfall-Blitz',
        titleLine2: 'geladen',
        body: 'Drück den grünen Button und lass die nächsten Hindernisse verschwinden!',
        icon: '/images/powerups/PowerUp_Batterie_Shift.png',
    },
    plane: {
        titleLine1: 'Berufs-Boost',
        titleLine2: 'aktiviert',
        body: 'Du bist für kurze Zeit unaufhaltbar!',
        icon: '/images/powerups/PowerUp_Star.png',
    },
}

const POWERUP_ICON_DROP_SHADOW =
    'drop-shadow-[1.714px_-1.714px_0px_var(--color-white-stroke-lightest),-1.714px_-1.714px_0px_var(--color-white-stroke-lightest),-1.714px_1.714px_0px_var(--color-white-stroke-lightest),1.714px_1.714px_0px_var(--color-white-stroke-lightest)]'

type TutorialOverlayCardProps = {
    variant: TutorialOverlayVariant
}

function TutorialOverlayCard({ variant }: TutorialOverlayCardProps) {
    const isStele = useSteleBreakpoint()
    const { titleLine1, titleLine2, body, icon } = TUTORIAL_OVERLAY_CONFIG[variant]
    const headlineFontSize = isStele ? 96 : 24
    const titleMaxCharsPerLine = Math.max(titleLine1.length, titleLine2.length)

    return (
        <div
            className={clsx(
                'relative w-full',
                isStele ? 'max-w-360' : 'max-w-80',
            )}
        >
            <div
                className={clsx(
                    'absolute inset-x-0 z-10 flex justify-center -translate-y-1/2',
                    isStele ? 'px-16' : 'px-6',
                )}
            >
                <div className='flex items-center'>
                    <div
                        className={clsx(
                            'relative shrink-0 transform-3d',
                            POWERUP_ICON_DROP_SHADOW,
                            isStele ? 'size-48' : 'size-12',
                            '-rotate-y-20 -rotate-x-6',
                        )}
                    >
                        <img
                            src={icon}
                            alt=''
                            aria-hidden
                            className='absolute inset-0 size-full object-contain pointer-events-none rotate-12'
                        />
                    </div>

                    <TextHeadline
                        colorPreset='blue'
                        fontSize={headlineFontSize}
                        leading={0.95}
                        maxCharsPerLine={titleMaxCharsPerLine}
                        tracking='-2%'
                        className={clsx(
                            'shrink-0',
                            isStele ? '-ml-48 -mr-56' : '-ml-12 -mr-14',
                        )}
                    >
                        {`${titleLine1} ${titleLine2}`}
                    </TextHeadline>
                </div>
            </div>

            <div
                className={clsx(
                    'relative flex flex-col items-center border border-black-stroke bg-white-stroke-even-darker pattern-background drop-shadow-2-black-stroke',
                    isStele
                        ? 'gap-8 rounded-[128px] px-24 pt-32 pb-24'
                        : 'gap-2 rounded-[32px] px-6 pt-10 pb-6',
                )}
            >
                <p
                    className={clsx(
                        'text-white-stroke-lightest font-nunito font-bold text-center tracking-[-2%]',
                        isStele
                            ? 'text-[64px] leading-22'
                            : 'text-[16px] leading-[22px]',
                    )}
                    style={{ textShadow: '0 1px 0 #06487C' }}
                >
                    {body}
                </p>
            </div>
        </div>
    )
}

const tapHintVariants = {
    hidden: { opacity: 0, scale: 0, y: 12 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: { type: 'spring' as const, stiffness: 420, damping: 20, mass: 0.7 },
    },
    exit: {
        opacity: 0,
        scale: 0.85,
        y: 8,
        transition: { duration: 0.15, ease: 'easeIn' as const },
    },
}

function TapToContinueHint() {
    const isStele = useSteleBreakpoint()

    return (
        <motion.div
            className={clsx(
                'flex items-center justify-center bg-white-gradient bg-clip-text text-transparent drop-shadow-[0_1px_0_var(--color-white-stroke)]',
                isStele ? 'gap-7 px-12 py-4' : 'gap-1.75 px-3 py-1',
            )}
            variants={tapHintVariants}
            initial='hidden'
            animate='visible'
            exit='exit'
        >
            <p
                className={clsx(
                    'font-grandstander font-bold italic uppercase tracking-[-4%]',
                    isStele ? 'text-[64px] leading-[88px]' : 'text-[16px] leading-[22px]',
                )}
            >
                Tippe zum weiterlaufen
            </p>
            <img
                src='/images/tutorial/tap-hint.svg'
                alt=''
                aria-hidden
                className={clsx(
                    'shrink-0 object-contain',
                    isStele ? 'size-16' : 'size-4',
                )}
            />
        </motion.div>
    )
}

const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2, ease: 'easeOut' as const } },
    exit: { opacity: 0, transition: { duration: 0.25, ease: 'easeIn' as const } },
}

const contentVariants = {
    hidden: { scale: 0, opacity: 0, y: 0 },
    visible: {
        scale: 1,
        opacity: 1,
        y: 0,
        transition: { type: 'spring' as const, stiffness: 380, damping: 22, mass: 0.8 },
    },
    exit: {
        scale: 1,
        opacity: 0,
        y: '40vh',
        transition: { duration: 0.28, ease: 'easeIn' as const },
    },
}

export type TutorialOverlaysProps = {
    className?: string
}

const TutorialOverlays = observer(function TutorialOverlays({
    className,
}: TutorialOverlaysProps) {
    const variant = store.powerUpTutorial.activeVariant
    const isStele = useSteleBreakpoint()
    const [canDismiss, setCanDismiss] = useState(false)

    useEffect(() => {
        if (!variant) {
            setCanDismiss(false)
            return
        }

        setCanDismiss(false)
        const timeoutId = window.setTimeout(() => {
            setCanDismiss(true)
        }, TAP_HINT_REVEAL_DELAY_MS)

        return () => window.clearTimeout(timeoutId)
    }, [variant])

    return (
        <AnimatePresence>
            {variant ? (
                <motion.div
                    key='power-up-tutorial-overlay'
                    className={clsx(
                        'z-[70] absolute inset-0 flex items-center justify-center bg-black/55 px-4 backdrop-blur-sm pointer-events-auto',
                        className,
                    )}
                    variants={backdropVariants}
                    initial='hidden'
                    animate='visible'
                    exit='exit'
                    onPointerDown={() => {
                        if (!canDismiss) return
                        store.powerUpTutorial.dismiss()
                    }}
                    role='dialog'
                    aria-modal='true'
                >
                    <motion.div
                        className={clsx(
                            'relative flex w-full max-w-full flex-col items-center',
                            isStele ? 'gap-16' : 'gap-4',
                        )}
                        variants={contentVariants}
                        initial='hidden'
                        animate='visible'
                        exit='exit'
                    >
                        <div
                            className='relative flex w-full justify-center'
                            onPointerDown={(event) => event.stopPropagation()}
                        >
                            <TutorialOverlayCard variant={variant} />
                        </div>
                        <div
                            className={clsx(
                                'flex items-center justify-center',
                                isStele ? 'min-h-24' : 'min-h-8',
                            )}
                        >
                            <AnimatePresence>
                                {canDismiss ? <TapToContinueHint key='tap-hint' /> : null}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    )
})

export default TutorialOverlays
